<script setup lang="ts">
import InputText from '@/components/core/form/InputText.vue';
import UiIcon from '@/components/core/UiIcon.vue';
import UiModal from '@/components/core/UiModal.vue';
import UiTouchButton from '@/components/core/UiTouchButton.vue';
import Icons from '@/constants/Icons';
import ModalNames from '@/constants/ModalNames';
import RouteNames from '@/constants/RouteNames';
import EmployeeModel from '@/models/employee/EmployeeModel';
import { getEmployeeByPin } from '@/services/employee';
import { useSessionStore } from '@/stores/session';
import { mdiArrowCollapseRight, mdiBackspace } from '@mdi/js';
import { onMounted, onUnmounted, ref } from 'vue';
import { useVfm } from 'vue-final-modal';
import { useRouter } from 'vue-router';

const { close } = useVfm();
const sessionStore = useSessionStore();
const router = useRouter();

const pin = ref('');
const badPin = ref(false);

const addDigit = (digit: string) => {
  if (pin.value.length < 6) {
    badPin.value = false;
    pin.value += digit;

    // Auto-validate when PIN is complete
    if (pin.value.length === 6) {
      handleValidatePin();
    }
  }
};

const removeLastDigit = () => {
  pin.value = pin.value.slice(0, -1);
};

const handleValidatePin = async () => {
  const response = await getEmployeeByPin(pin.value);
  if (response.error) {
    badPin.value = true;
  } else if (response.data) {
    const newEmployee = new EmployeeModel(response.data[0]);

    // Check if we're in a movement form and if the employee has changed
    if (sessionStore.lastPinEmployee && sessionStore.lastPinEmployee.uid !== newEmployee.uid) {
      // Employee has changed in movement form, redirect to movement list
      sessionStore.setLastPinEmployee(newEmployee);
      close(ModalNames.PinLogin);
      router.push({ name: RouteNames.Movement.List });
      return;
    }

    // Set the new employee for display and remember them as the last PIN employee
    sessionStore.setLastPinEmployee(newEmployee);
    close(ModalNames.PinLogin);
  }

  pin.value = '';
};

const handleKeydown = (event: KeyboardEvent) => {
  if (/^[0-9]$/.test(event.key)) {
    addDigit(event.key);
  } else if (event.key === 'Backspace') {
    removeLastDigit();
  } else if (event.key === 'Enter') {
    handleValidatePin();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
  });
});
</script>

<template>
  <UiModal :icon="Icons.PinLogin" :modalId="ModalNames.PinLogin" sizeClass="w-md tablet:w-xs desktop:w-sm" required>
    <div class="space-y-8 p-8">
      <InputText v-model="pin" class="w-full text-center font-medium" huge :invalid="badPin" maxlength="6" readonly type="text" />
      <div role="group" aria-label="Dialpad" class="ui-bg-primary grid grid-cols-3 gap-1 rounded p-1 *:aspect-square *:text-4xl *:font-semibold">
        <UiTouchButton flex @pointerdown="addDigit('1')">1</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('2')">2</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('3')">3</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('4')">4</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('5')">5</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('6')">6</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('7')">7</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('8')">8</UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('9')">9</UiTouchButton>
        <UiTouchButton flex @pointerdown="removeLastDigit">
          <UiIcon :path="mdiBackspace" />
        </UiTouchButton>
        <UiTouchButton flex @pointerdown="addDigit('0')">0</UiTouchButton>
        <UiTouchButton flex @pointerdown="handleValidatePin">
          <UiIcon :path="mdiArrowCollapseRight" />
        </UiTouchButton>
      </div>
    </div>
  </UiModal>
</template>
